import dayjs from 'dayjs';
import { ClientAuthToken, CoachSocialLoginResult, parseClientAuthTokenResponse } from 'hevy-shared';
import Cookies from 'js-cookie';
import router from 'next/router';
import API from 'utils/API';
import { fireAndForget } from 'utils/async';
import { refreshUserContent } from 'utils/fetchUserContent';

export class Auth {
  private get adminAuthCookie() {
    return Cookies.get('admin-auth2.0-token');
  }

  get isImpersonating() {
    return !!this.adminAuthCookie;
  }

  get isAuthenticated(): boolean {
    return !!Cookies.get('auth2.0-token') || !!Cookies.get('auth-token');
  }

  get authToken(): string | undefined {
    return Cookies.get('auth-token');
  }

  login = async (
    usernameOrEmail: string,
    password: string,
    inviteShortId?: string,
  ): Promise<{ is_first_login_to_coach_platform: boolean }> => {
    const result = await API.login(usernameOrEmail, password, inviteShortId);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
    return { is_first_login_to_coach_platform: result.data.is_first_login_to_coach_platform };
  };

  loginWithTempToken = async (
    tempToken: string,
  ): Promise<{ is_first_login_to_coach_platform: boolean }> => {
    const result = await API.loginWithTempToken(tempToken);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
    return { is_first_login_to_coach_platform: result.data.is_first_login_to_coach_platform };
  };

  impersonateUser = async (userId: string): Promise<void> => {
    const result = await API.postImpersonateUser(userId);

    const currentAuthCookie = Cookies.get('auth2.0-token');

    if (!currentAuthCookie) {
      return;
    }

    Cookies.set('admin-auth2.0-token', currentAuthCookie, {
      expires: dayjs().add(10, 'year').toDate(),
    });

    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
  };

  stopImpersonatingUser = async (): Promise<void> => {
    const { adminAuthCookie } = this;

    if (!adminAuthCookie) {
      this.logout();
      return;
    }

    await API.logout();

    this.setAuthCookie(parseClientAuthTokenResponse(JSON.parse(adminAuthCookie)));
    Cookies.remove('admin-auth2.0-token');

    await refreshUserContent();
    router.reload();
  };

  signup = async (
    username: string,
    email: string,
    password: string,
    isTermsAccepted: boolean,
    inviteShortId?: string,
  ) => {
    const result = await API.signup(username, email, password, isTermsAccepted, inviteShortId);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
  };

  signupWithTempToken = async (tempToken: string) => {
    const result = await API.signupFromTemporaryToken(tempToken);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
  };

  loginWithGoogle = async (
    code: string,
    inviteShortId?: string,
  ): Promise<CoachSocialLoginResult> => {
    const result = await API.signInWithGoogle(code, inviteShortId);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
    return result.data;
  };

  loginWithApple = async (
    identityToken: string,
    email?: string,
    inviteShortId?: string,
  ): Promise<CoachSocialLoginResult> => {
    const result = await API.signInWithApple(identityToken, email, inviteShortId);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
    return result.data;
  };

  authMigrate = async () => {
    if (!!Cookies.get('auth2.0-token')) return;

    console.log('Migrating to Auth 2.0');
    const result = await API.authMigrate();
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
  };

  logout = async () => {
    Cookies.remove('auth2.0-token');
    Cookies.remove('access-token');
    Cookies.remove('auth-token');
  };

  userLogout = async () => {
    await fireAndForget([API.logout()]);
    return this.logout();
  };

  private setAuthCookie = (newToken: ClientAuthToken | null) => {
    const { access_token, refresh_token, expires_at } = newToken || {};

    const auth2_0Token =
      !!access_token && !!refresh_token && !!expires_at
        ? { access_token, refresh_token, expires_at: new Date(expires_at) }
        : null;

    Cookies.set('auth2.0-token', JSON.stringify(auth2_0Token), {
      expires: dayjs().add(10, 'year').toDate(),
    });

    if (access_token) {
      // This is used by the server side logic.
      Cookies.set('access-token', access_token, { expires: dayjs().add(10, 'year').toDate() });
    } else {
      Cookies.remove('access-token');
    }

    Cookies.remove('auth-token');
  };

  onNewAuthToken = (newToken: ClientAuthToken) => {
    Cookies.set('auth2.0-token', JSON.stringify(newToken), {
      expires: dayjs().add(10, 'year').toDate(),
    });

    Cookies.set('access-token', newToken.access_token, {
      expires: dayjs().add(10, 'year').toDate(),
    });
  };
}
